import { createSchema, encodeFieldKey, extractDataType } from './validation/ValidationSchemaGenerator';
import { Components, Field, Form, isField, Scene } from './sceneDataSchema';
import { ComponentsTypes } from '@/constants/componentsTypes';
import { ListItemTypes } from '@/constants/listItemTypes';
import { convertValue } from './converters';
import { FormValues } from '@/constants/formTypes';

export interface FormServerError {
  key: string;
  message: string;
}

const generateDefaultValues = (inputFields: Field[]): FormValues => {
  return Object.fromEntries(
    inputFields.filter(isField).map((field) => {
      const convertedValue = convertValue(field.value, extractDataType(field));
      return [encodeFieldKey(field), convertedValue];
    })
  );
};

export const findForm = (components: Components, formId: string | null = null): Form | null => {
  for (const component of components) {
    if (component.type === ComponentsTypes.FORM && (formId === null || component.id === formId)) {
      return component;
    }
    if (component.type === ComponentsTypes.LAYOUT) {
      const foundForm = findForm(component.components, formId);
      if (foundForm) {
        return foundForm;
      }
    }
  }
  return null;
};

export const getForm = (data: Scene, formId: string | null = null): Form => {
  const form = findForm(data.components, formId);

  if (!form) {
    throw new Error('Form not found');
  }
  return form;
};

export class FormConfigResolver {
  public inputFields: Field[] = [];

  constructor(components: Components) {
    this.inputFields = [];
    this.findInputFields(components);
  }

  findInputFields(components: Components) {
    components.map((component) => {
      if (
        component.type === ComponentsTypes.LAYOUT ||
        component.type === ComponentsTypes.FORM ||
        component.type === ComponentsTypes.BIND_FORM ||
        component.type === ComponentsTypes.CARD ||
        component.type === ComponentsTypes.CARD_CONTENT ||
        component.type === ComponentsTypes.ACCORDION ||
        component.type === ComponentsTypes.TAB_PANEL ||
        component.type === ComponentsTypes.FIXED_CONTAINER ||
        component.type === ComponentsTypes.CAROUSEL_TILE ||
        component.type === ComponentsTypes.LIST ||
        component.type === ComponentsTypes.READ_MORE ||
        component.type === ComponentsTypes.EXPANDABLE_LIST
      ) {
        return this.findInputFields(component.components);
      }

      if (component.type === ComponentsTypes.MODAL && component.modal_body) {
        // Assuming no form fields in modal_footer
        return this.findInputFields(component.modal_body);
      }

      if (component.type === ComponentsTypes.CAROUSEL && component.tiles) {
        return this.findInputFields(component.tiles);
      }

      if (component.type === ComponentsTypes.EXPANDABLE_CONTAINER && component.details) {
        // Assuming no form fields in summary
        return this.findInputFields(component.details);
      }

      if (component.type === ComponentsTypes.ACCORDION_LIST) {
        return this.findInputFields(component.accordions);
      }

      if (component.type === ComponentsTypes.ADDRESS) {
        return this.findInputFields([
          component.address1,
          component.address2,
          component.city,
          component.state,
          component.zip,
        ]);
      }

      if (component.type === ComponentsTypes.ALT_ADDRESS) {
        return this.findInputFields([
          component.address1,
          component.address2,
          component.city,
          component.state,
          component.zip,
        ]);
      }

      if (component.type === ComponentsTypes.ANNUALIZED_MILEAGE) {
        return this.findInputFields([
          component.primary_usage,
          component.annualized_mileage,
          component.commute_distance,
        ]);
      }
      if (component.type === ListItemTypes.TOGGLE && !!component.toggle_component) {
        this.inputFields.push(component.toggle_component);
      }

      if (isField(component)) {
        this.inputFields.push(component);
      }
    });
  }

  formSchema() {
    return createSchema(this.inputFields);
  }

  defaultValues(): FormValues {
    return generateDefaultValues(this.inputFields);
  }

  maskedFields() {
    return this.inputFields
      .filter(isField)
      .filter((field) => field.masked && field.value !== null)
      .map(encodeFieldKey);
  }

  invalidFields(): FormServerError[] {
    return this.inputFields
      .filter(isField)
      .filter((field) => field.valid === false && field.value !== null)
      .map((field) => ({
        key: encodeFieldKey(field),
        message: field.message ?? 'validation error on the server',
      }));
  }
}
